import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { SxProps, Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Avatar from '@mui/material/Avatar'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import NoMeetingRoomIcon from '@mui/icons-material/NoMeetingRoom'
import CircularProgress from '@mui/material/CircularProgress'

import { MyShift } from '@/core/models/planning.model'
import { toAdapterLocale } from '@/utils/locale'

dayjs.extend(utc)

export type ShiftDashboardCardProps = {
    upcomingShifts: MyShift[]
    isLoading: boolean
    error?: any
    sx?: SxProps<Theme>
}

export default function ShiftDashboardCard(props: ShiftDashboardCardProps) {
    const { upcomingShifts, isLoading, error, sx = [] } = props
    const { t, i18n } = useTranslation()
    const locale = toAdapterLocale(i18n.language)

    const getShiftTimeLabel = (shift: MyShift) => {
        const startDate = dayjs.utc(shift.startTime).locale(locale)
        const today = dayjs.utc().startOf('day')
        const tomorrow = today.add(1, 'day')

        if (startDate.isSame(today, 'day')) {
            return t('common.today', 'Today')
        } else if (startDate.isSame(tomorrow, 'day')) {
            return t('common.tomorrow', 'Tomorrow')
        } else if (startDate.isAfter(today) && startDate.isBefore(today.add(7, 'day'))) {
            return startDate.format('dddd')
        } else {
            return startDate.format('MMM DD')
        }
    }

    const getShiftStatusColor = (shift: MyShift) => {
        const startDate = dayjs.utc(shift.startTime)
        const today = dayjs.utc().startOf('day')

        if (startDate.isSame(today, 'day')) {
            return 'error.main'
        } else if (startDate.isSame(today.add(1, 'day'), 'day')) {
            return 'warning.main'
        } else {
            return 'info.main'
        }
    }

    const renderShiftItem = (shift: MyShift) => {
        const startDate = dayjs.utc(shift.startTime).locale(locale)
        const endDate = dayjs.utc(shift.endTime).locale(locale)

        return (
            <Box key={shift.shiftId} sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip
                        label={getShiftTimeLabel(shift)}
                        size="small"
                        sx={{
                            bgcolor: getShiftStatusColor(shift),
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        {shift.planningName}
                    </Typography>
                </Stack>

                <Stack spacing={1} sx={{ ml: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {startDate.format('HH:mm')} - {endDate.format('HH:mm')}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {shift.location}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <MedicalServicesIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {shift.consultationReason}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        )
    }

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </Box>
            )
        }

        if (error) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', flexDirection: 'column' }}>
                    <Typography color="error" gutterBottom>
                        {t('common.errorOccurred', 'An error occurred')}
                    </Typography>
                </Box>
            )
        }

        if (!upcomingShifts || upcomingShifts.length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', flexDirection: 'column' }}>
                    <NoMeetingRoomIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                        {t('page.home.noUpcomingShifts', 'No upcoming shifts')}
                    </Typography>
                </Box>
            )
        }

        return (
            <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                {upcomingShifts.map(renderShiftItem)}
            </Box>
        )
    }

    return (
        <Card sx={[{ height: '100%', display: 'flex', flexDirection: 'column' }, ...(Array.isArray(sx) ? sx : [sx])]}>
            <CardHeader
                title={t('page.home.upcomingShifts', 'Upcoming Shifts')}
                avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><CalendarTodayIcon /></Avatar>}
            />

            <Divider />

            <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
                {renderContent()}
            </CardContent>

            <Divider />

            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button
                    component={Link}
                    to="/shift-marketplace"
                    color="primary"
                    variant="outlined"
                >
                    {t('page.home.viewAllShifts', 'View All Shifts')}
                </Button>
            </CardActions>
        </Card>
    )
}
